import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Edit, TrendingUp, ShoppingCart, Users, Package,
  DollarSign, Activity, Calendar, MapPin, Phone, Mail, Globe,
  CheckCircle, XCircle, BarChart3, Gift, CreditCard
} from 'lucide-react';
import api from '../../services/api';
import BillingTab from './BillingTab';
import { t, locale } from '../../i18n'

export default function RestaurantDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadRestaurantDetails();
  }, [id]);

  const loadRestaurantDetails = async () => {
    try {
      setLoading(true);
      const [restaurantRes, analyticsRes] = await Promise.all([
        api.get(`/api/restaurants/${id}`),
        api.get(`/api/restaurants/${id}/analytics`)
      ]);
      
      setRestaurant(restaurantRes.data.restaurant);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to load restaurant details:', error);
      alert(t("Failed to load restaurant details"));
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async () => {
    const action = restaurant.is_active ? 'suspend' : 'reactivate';
    if (!window.confirm(t("Are you sure you want to {action} \"{name}\"?", { action: t(action), name: restaurant.name }))) return;
    try {
      if (restaurant.is_active) {
        await api.put(`/api/restaurants/${id}`, { is_active: false });
      } else {
        await api.post(`/api/restaurants/${id}/activate`);
      }
      loadRestaurantDetails();
    } catch (error) {
      console.error(`Error trying to ${action} restaurant:`, error);
      alert(t("Failed to {action} restaurant", { action: t(action) }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">{t("Restaurant Not Found")}</h2>
        <button
          onClick={() => navigate('/owner')}
          className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
        >
          {t("Back to Dashboard")}
        </button>
      </div>
    );
  }

  const settings = typeof restaurant.settings === 'string' 
    ? JSON.parse(restaurant.settings) 
    : restaurant.settings || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/owner')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            {t("Back to Dashboard")}
          </button>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-slate-900">{restaurant.name}</h1>
                {restaurant.is_active ? (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    {t("Active")}
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium flex items-center gap-1">
                    <XCircle className="w-4 h-4" />
                    {t("Inactive")}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {restaurant.address}
                </span>
                {restaurant.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {restaurant.phone}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={toggleActive}
                className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 shadow-lg ${
                  restaurant.is_active
                    ? 'bg-red-50 text-red-600 hover:bg-red-100 shadow-red-500/10'
                    : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 shadow-emerald-500/10'
                }`}
              >
                {restaurant.is_active ? <XCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                {restaurant.is_active ? t("Suspend") : t("Reactivate")}
              </button>
              <button
                onClick={() => navigate(`/owner/restaurants/${id}/edit`)}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 font-medium transition-all flex items-center gap-2 shadow-lg shadow-blue-500/30"
              >
                <Edit className="w-5 h-5" />
                {t("Edit Restaurant")}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <DollarSign className="w-6 h-6" />
              </div>
              <TrendingUp className="w-5 h-5 opacity-80" />
            </div>
            <div className="text-3xl font-bold mb-1">
              {settings.currency || 'OMR'} {analytics?.totalRevenue?.toLocaleString(locale()) || '0'}
            </div>
            <div className="text-blue-100">{t("Total Revenue")}</div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <ShoppingCart className="w-6 h-6" />
              </div>
              <Activity className="w-5 h-5 opacity-80" />
            </div>
            <div className="text-3xl font-bold mb-1">
              {analytics?.totalOrders?.toLocaleString(locale()) || '0'}
            </div>
            <div className="text-green-100">{t("Total Orders")}</div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Package className="w-6 h-6" />
              </div>
              <BarChart3 className="w-5 h-5 opacity-80" />
            </div>
            <div className="text-3xl font-bold mb-1">
              {analytics?.totalMenuItems?.toLocaleString(locale()) || '0'}
            </div>
            <div className="text-purple-100">{t("Menu Items")}</div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Users className="w-6 h-6" />
              </div>
              <Users className="w-5 h-5 opacity-80" />
            </div>
            <div className="text-3xl font-bold mb-1">
              {analytics?.totalEmployees?.toLocaleString(locale()) || '0'}
            </div>
            <div className="text-orange-100">{t("Employees")}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="border-b border-slate-200">
            <div className="flex gap-1 p-2 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: Activity },
                { id: 'billing', label: 'Billing', icon: CreditCard },
                { id: 'products', label: 'Top Products', icon: Package },
                { id: 'loyalty', label: 'Loyalty', icon: Gift },
                { id: 'employees', label: 'Employees', icon: Users },
                { id: 'inventory', label: 'Inventory', icon: Package }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {t(tab.label)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-8">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{t("Restaurant Information")}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-slate-500">{t("Branch Code")}</label>
                        <p className="text-slate-900 font-medium">{restaurant.code}</p>
                      </div>
                      <div>
                        <label className="text-sm text-slate-500">{t("Email")}</label>
                        <p className="text-slate-900 font-medium">{restaurant.email || t("Not set")}</p>
                      </div>
                      <div>
                        <label className="text-sm text-slate-500">{t("Currency")}</label>
                        <p className="text-slate-900 font-medium">{settings.currency || 'OMR'}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-slate-500">{t("Tax Rate")}</label>
                        <p className="text-slate-900 font-medium">{settings.tax_rate || 0}%</p>
                      </div>
                      <div>
                        <label className="text-sm text-slate-500">{t("Service Charge")}</label>
                        <p className="text-slate-900 font-medium">{settings.service_charge || 0}%</p>
                      </div>
                      <div>
                        <label className="text-sm text-slate-500">{t("Timezone")}</label>
                        <p className="text-slate-900 font-medium">{settings.timezone || t("Not set")}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{t("Recent Activity")}</h3>
                  <div className="space-y-3">
                    {analytics?.recentOrders?.length > 0 ? (
                      analytics.recentOrders.map((order) => (
                        <div key={order.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                          <div>
                            <p className="font-medium text-slate-900">{t("Order #")}{order.order_code}</p>
                            <p className="text-sm text-slate-500">
                              {new Date(order.created_at).toLocaleDateString(locale())} {t("at")} {new Date(order.created_at).toLocaleTimeString(locale())}
                            </p>
                          </div>
                          <div className="text-end">
                            <p className="font-bold text-slate-900">{settings.currency} {Number(order.total || 0).toFixed(2)}</p>
                            <p className="text-sm text-slate-500">{t(order.status)}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-500 text-center py-8">{t("No recent orders")}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{t("Top Selling Products")}</h3>
                <div className="space-y-3">
                  {analytics?.topProducts?.length > 0 ? (
                    analytics.topProducts.map((product, index) => (
                      <div key={product.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-4">
                          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{product.name}</p>
                            <p className="text-sm text-slate-500">{product.category_name}</p>
                          </div>
                        </div>
                        <div className="text-end">
                          <p className="font-bold text-slate-900">{product.total_sold || 0} {t("sold")}</p>
                          <p className="text-sm text-slate-500">{settings.currency} {Number(product.price || 0).toFixed(2)}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 text-center py-8">{t("No product data available")}</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'employees' && (
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{t("Employee List")}</h3>
                <div className="space-y-3">
                  {analytics?.employees?.length > 0 ? (
                    analytics.employees.map((employee) => (
                      <div key={employee.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div>
                          <p className="font-medium text-slate-900">{employee.full_name}</p>
                          <p className="text-sm text-slate-500">{employee.role} • {employee.email}</p>
                        </div>
                        <div className="text-end">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            employee.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {employee.is_active ? t("Active") : t("Inactive")}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 text-center py-8">{t("No employees found")}</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'billing' && <BillingTab restaurantId={id} />}

            {activeTab === 'loyalty' && (
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{t("Loyalty Program")}</h3>
                {analytics?.loyalty ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-2xl font-bold text-slate-900">{analytics.loyalty.totalMembers}</p>
                      <p className="text-sm text-slate-500">{t("Total members")}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-2xl font-bold text-slate-900">{analytics.loyalty.newMembersLast30Days}</p>
                      <p className="text-sm text-slate-500">{t("New (30 days)")}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-2xl font-bold text-slate-900">{analytics.loyalty.revenueFromMembersPct}%</p>
                      <p className="text-sm text-slate-500">{t("Revenue from members")}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-2xl font-bold text-emerald-600">{analytics.loyalty.pointsIssued.toLocaleString(locale())}</p>
                      <p className="text-sm text-slate-500">{t("Points issued")}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-2xl font-bold text-orange-600">{analytics.loyalty.pointsRedeemed.toLocaleString(locale())}</p>
                      <p className="text-sm text-slate-500">{t("Points redeemed")}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-2xl font-bold text-slate-900">{analytics.loyalty.outstandingPointsLiability.toLocaleString(locale())}</p>
                      <p className="text-sm text-slate-500">{t("Outstanding balance (all members)")}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">{t("No loyalty data yet")}</p>
                )}
              </div>
            )}

            {activeTab === 'inventory' && (
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{t("Inventory Status")}</h3>
                <div className="space-y-3">
                  {analytics?.inventory?.length > 0 ? (
                    analytics.inventory.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div>
                          <p className="font-medium text-slate-900">{item.name}</p>
                          <p className="text-sm text-slate-500">{item.sku || t("No SKU")}</p>
                        </div>
                        <div className="text-end">
                          <p className="font-bold text-slate-900">{item.quantity || 0} {t(item.unit)}</p>
                          {item.quantity <= item.min_threshold && (
                            <span className="text-xs text-red-600 font-medium">{t("Low Stock")}</span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 text-center py-8">{t("No inventory items")}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
